import { Key, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { SidebarLinkLegacy } from './AppSidebarMenuItem';
import { AppSidebarMoreMenu } from './AppSidebarMoreMenu';
import { AppSidebarMenuGroup } from './AppSidebarMenuGroup';

const MenuContainer = styled.div`
  flex: 1;
  min-height: 0;
  padding: 0;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  overflow: hidden;
`;

const OVERFLOW_BUFFER = 40;
const DEFAULT_ITEM_HEIGHT = 52;
const GUTTER_HEIGHT = 20;

function getItemHeight(key: Key, cachedHeights: Map<Key, number>): number {
  const cached = cachedHeights.get(key);
  if (cached) return cached;
  return String(key).startsWith('gutter') ? GUTTER_HEIGHT : DEFAULT_ITEM_HEIGHT;
}

function getVisibleLinks(links: SidebarLinkLegacy[]): SidebarLinkLegacy[] {
  return links.filter((link) => !link.hidden);
}

function hideTrailingGutters(keys: Key[], overflowedKeys: Set<Key>) {
  const isGutter = (k: Key) => String(k).startsWith('gutter');
  for (const [i, key] of keys.entries()) {
    if (isGutter(key) && !overflowedKeys.has(key)) {
      const linksAfter = keys.slice(i + 1).filter((k) => !isGutter(k));
      const allLinksAfterOverflowed = linksAfter.every((k) =>
        overflowedKeys.has(k),
      );
      if (allLinksAfterOverflowed) {
        overflowedKeys.add(key);
      }
    }
  }
}

interface AppSidebarMenuLegacyProps {
  homeLinks: SidebarLinkLegacy[];
  offersLinks: SidebarLinkLegacy[];
  primaryLinks: SidebarLinkLegacy[];
  secondaryLinks: SidebarLinkLegacy[];
}

export function AppSidebarMenu({
  homeLinks,
  offersLinks,
  primaryLinks,
  secondaryLinks,
}: AppSidebarMenuLegacyProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<Key, HTMLElement>>(new Map());
  const itemHeights = useRef<Map<Key, number>>(new Map());
  const fullContentHeightRef = useRef<number>(0);
  const overflowedKeysRef = useRef<Set<Key>>(new Set());
  const [overflowedLinkKeys, setOverflowedLinkKeys] = useState<Set<Key>>(
    new Set(),
  );

  const visibleHomeLinks = getVisibleLinks(homeLinks);
  const visibleOffersLinks = getVisibleLinks(offersLinks);
  const visiblePrimaryLinks = getVisibleLinks(primaryLinks);
  const visibleSecondaryLinks = getVisibleLinks(secondaryLinks);

  const allItemKeys = useRef<Key[]>([]);
  allItemKeys.current = [
    ...visibleHomeLinks.map((l) => l.key),
    ...(visibleHomeLinks.length > 0 ? ['gutter-0'] : []),
    ...visibleOffersLinks.map((l) => l.key),
    'gutter-1',
    ...visiblePrimaryLinks.map((l) => l.key),
    'gutter-2',
    ...visibleSecondaryLinks.map((l) => l.key),
  ];

  const allLinksRef = useRef<SidebarLinkLegacy[]>([]);
  allLinksRef.current = [
    ...visibleHomeLinks,
    ...visibleOffersLinks,
    ...visiblePrimaryLinks,
    ...visibleSecondaryLinks,
  ];

  const checkOverflow = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();

    if (containerRect.height <= 0) return;

    for (const key of allItemKeys.current) {
      const itemElement = itemRefs.current.get(key);
      if (itemElement) {
        const { height } = itemElement.getBoundingClientRect();
        if (height > 0) {
          itemHeights.current.set(key, height);
        }
      }
    }

    if (overflowedKeysRef.current.size === 0 && contentRef.current) {
      fullContentHeightRef.current = contentRef.current.scrollHeight;
    }

    let totalContentHeight = fullContentHeightRef.current;
    if (totalContentHeight === 0) {
      for (const key of allItemKeys.current) {
        totalContentHeight += getItemHeight(key, itemHeights.current);
      }
    }

    if (totalContentHeight <= containerRect.height) {
      if (overflowedKeysRef.current.size > 0) {
        fullContentHeightRef.current = 0;
        overflowedKeysRef.current = new Set();
        setOverflowedLinkKeys(new Set());
        requestAnimationFrame(() => checkOverflow());
      }
      return;
    }

    const availableHeight =
      containerRect.height - DEFAULT_ITEM_HEIGHT - OVERFLOW_BUFFER;

    const overflowedKeys = new Set<Key>();
    let accumulatedHeight = 0;
    let hasOverflowed = false;

    for (const key of allItemKeys.current) {
      const height = getItemHeight(key, itemHeights.current);

      if (hasOverflowed || accumulatedHeight + height > availableHeight) {
        overflowedKeys.add(key);
        hasOverflowed = true;
      } else {
        accumulatedHeight += height;
      }
    }

    hideTrailingGutters(allItemKeys.current, overflowedKeys);

    overflowedKeysRef.current = overflowedKeys;
    setOverflowedLinkKeys(overflowedKeys);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    let isMounted = true;
    const container = containerRef.current;

    const handleResize = () => {
      if (isMounted) checkOverflow();
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    window.addEventListener('resize', handleResize);

    const timeout = setTimeout(handleResize, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [checkOverflow]);

  const overflowedLinks = allLinksRef.current.filter((link) =>
    overflowedLinkKeys.has(link.key),
  );

  return (
    <MenuContainer ref={containerRef}>
      <ContentWrapper ref={contentRef}>
        {visibleHomeLinks.length > 0 && (
          <AppSidebarMenuGroup
            links={homeLinks}
            overflowedLinkKeys={overflowedLinkKeys}
            itemRefs={itemRefs}
            gutterId="gutter-0"
          />
        )}
        <AppSidebarMenuGroup
          links={offersLinks}
          overflowedLinkKeys={overflowedLinkKeys}
          itemRefs={itemRefs}
          gutterId="gutter-1"
        />
        <AppSidebarMenuGroup
          links={primaryLinks}
          overflowedLinkKeys={overflowedLinkKeys}
          itemRefs={itemRefs}
          gutterId="gutter-2"
        />
        <AppSidebarMenuGroup
          links={secondaryLinks}
          overflowedLinkKeys={overflowedLinkKeys}
          itemRefs={itemRefs}
          disableGutter={true}
        />
      </ContentWrapper>
      <AppSidebarMoreMenu links={overflowedLinks} />
    </MenuContainer>
  );
}
